// Edits one job's WhatsApp pre-screen message.
//
// Saving a message switches the pre-screen ON for the job: the Screening tab
// gets a WhatsApp button per candidate, the "To contact / Messaged /
// Interested" chips, and "Invite to AI Interview" waits until a candidate has
// agreed. Removing the message switches all of that off again and the job
// invites straight from screening, as before. That is said in the dialog,
// because it is the one consequence somebody editing text would not expect.
//
// Lives in the pipeline rather than in the job form on purpose. The job form
// also edits the job's rounds, which carry the rubrics, and a message box is
// not a reason to put that form's save in the path of this feature.
//
// LAYOUT
// A fixed header and footer with the two panes between them, each scrolling
// on its own at the same height. The first version let the preview grow with
// the message, which pushed Save and the app/web choice off the bottom of a
// laptop screen and left the two panes at different heights.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CloseIcon from "@mui/icons-material/Close";
import {
  CHAT_OPENERS,
  PLACEHOLDERS,
  buildPrescreenMessage,
  suggestedTemplate,
  unfilledPlaceholders,
} from "../utils/prescreenMessage";

const FONT = "'Plus Jakarta Sans', sans-serif";
const INK = "#18181b";
const BODY = "#3f3f46";
const MUTED = "#71717a";
const FAINT = "#a1a1aa";
const HAIR = "#f4f4f5";
const LINE = "#e4e4e7";
const TEAL = "#128c7e";

// The panes' height. Fits a 768px-tall laptop with the header and footer.
const PANE_H = { xs: 260, md: 380 };

// WhatsApp's own formatting — *bold*, _italic_, ~strike~ — drawn the way
// WhatsApp draws it, so the preview is what the candidate sees rather than a
// message full of asterisks. Line by line; markers never span a line break,
// which is WhatsApp's rule too.
function renderWhatsApp(text) {
  return String(text)
    .split("\n")
    .map((line, i) => (
      <React.Fragment key={i}>
        {i > 0 && <br />}
        {line.split(/(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g).map((part, j) => {
          if (/^\*[^*]+\*$/.test(part)) return <strong key={j}>{part.slice(1, -1)}</strong>;
          if (/^_[^_]+_$/.test(part)) return <em key={j}>{part.slice(1, -1)}</em>;
          if (/^~[^~]+~$/.test(part)) return <s key={j}>{part.slice(1, -1)}</s>;
          return <React.Fragment key={j}>{part}</React.Fragment>;
        })}
      </React.Fragment>
    ));
}

const textBtnSx = {
  fontFamily: FONT,
  textTransform: "none",
  fontWeight: 650,
  fontSize: 13,
  color: MUTED,
  minWidth: 0,
};

export default function WhatsAppTemplateDialog({
  open,
  onClose,
  job,
  sampleRow,
  onSave,
  // Where chats open on this computer ("app" | "web"), or null to hide the
  // choice (the phone app). Saved per browser the moment it is picked — it
  // is not part of the job's message and does not wait for "Save".
  opener,
  onOpenerChange,
}) {
  const current = job?.whatsapp_template || "";
  const [draft, setDraft] = useState(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Fresh each time it opens: the saved message, or a suggestion built from
  // the job when there is none yet.
  useEffect(() => {
    if (!open) return;
    setDraft(current || suggestedTemplate(job || {}));
    setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Previewed against a real candidate from this job, so "{name}" is seen
  // the way a candidate will read it.
  const preview = useMemo(
    () =>
      buildPrescreenMessage(sampleRow || { candidate_name: "Priya Sharma" }, {
        ...(job || {}),
        whatsapp_template: draft,
      }),
    [draft, job, sampleRow],
  );
  const unfilled = unfilledPlaceholders(preview);

  // Drops a placeholder where the cursor is, rather than making somebody type
  // braces correctly. Focus and cursor go back to just after it.
  const insert = (key) => {
    const el = inputRef.current;
    const token = `{${key}}`;
    const start = el?.selectionStart ?? draft.length;
    const end = el?.selectionEnd ?? draft.length;
    const next = draft.slice(0, start) + token + draft.slice(end);
    setDraft(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const save = async (value) => {
    setSaving(true);
    setError(null);
    try {
      await onSave(value);
      onClose();
    } catch (err) {
      // Kept open with the text intact; losing a message somebody wrote to a
      // failed save is the worst way for this to fail.
      setError(err.response?.data?.statusMessage || err.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const paneLabel = (text) => (
    <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 750, letterSpacing: 0.6, color: FAINT }}>
      {text}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={1.25}
        sx={{ px: 3, pt: 2.25, pb: 1.75, borderBottom: `1px solid ${HAIR}` }}
      >
        <WhatsAppIcon sx={{ color: "#25d366", fontSize: 22, mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 16, fontWeight: 750, color: INK }}>
            WhatsApp message
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: MUTED, mt: 0.25 }} noWrap>
            {job?.title || "This job"} · sent before the AI interview — only candidates marked
            Interested can be invited
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={saving} aria-label="Close">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>

      {/* ── The message, and what it looks like ────────────────────────── */}
      <Box sx={{ px: 3, py: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, fontFamily: FONT, fontSize: 13 }}>
            {error}
          </Alert>
        )}

        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          {/* Editor */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
              {paneLabel("MESSAGE")}
              <Button
                size="small"
                onClick={() => setDraft(suggestedTemplate(job || {}))}
                disabled={saving}
                sx={{ ...textBtnSx, fontSize: 12, py: 0 }}
              >
                Use suggestion
              </Button>
            </Stack>
            <Box
              sx={{
                height: PANE_H,
                display: "flex",
                flexDirection: "column",
                border: `1px solid ${LINE}`,
                borderRadius: 2,
                overflow: "hidden",
                "&:focus-within": { borderColor: TEAL },
              }}
            >
              <Box
                component="textarea"
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={saving}
                maxLength={4000}
                spellCheck
                sx={{
                  flex: 1,
                  width: "100%",
                  resize: "none",
                  border: 0,
                  outline: 0,
                  p: 1.75,
                  fontFamily: FONT,
                  fontSize: 13.5,
                  lineHeight: 1.6,
                  color: INK,
                  bgcolor: "#fff",
                }}
              />
              {/* Insert, rather than type: braces typed by hand are where
                  "{nmae}" comes from. */}
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  flexWrap: "wrap",
                  gap: 0.5,
                  px: 1.25,
                  py: 0.9,
                  borderTop: `1px solid ${HAIR}`,
                  bgcolor: "#fafafa",
                }}
              >
                <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: FAINT, mr: 0.25 }}>
                  Insert
                </Typography>
                {PLACEHOLDERS.map((p) => (
                  <Box
                    key={p.key}
                    component="button"
                    type="button"
                    title={p.hint}
                    onClick={() => insert(p.key)}
                    disabled={saving}
                    sx={{
                      border: `1px solid ${LINE}`,
                      bgcolor: "#fff",
                      borderRadius: 1.5,
                      px: 0.75,
                      py: 0.2,
                      fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                      fontSize: 11,
                      color: BODY,
                      cursor: "pointer",
                      "&:hover": { borderColor: TEAL, color: TEAL },
                    }}
                  >
                    {`{${p.key}}`}
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>

          {/* Preview — a chat bubble on WhatsApp's wallpaper colour. */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ mb: 0.75, minHeight: 26, display: "flex", alignItems: "center" }}>
              {paneLabel(
                `PREVIEW${sampleRow?.candidate_name ? ` · AS ${String(sampleRow.candidate_name).toUpperCase()}` : ""}`,
              )}
            </Box>
            <Box
              sx={{
                height: PANE_H,
                overflowY: "auto",
                borderRadius: 2,
                bgcolor: "#efeae2",
                p: 1.75,
              }}
            >
              <Box
                sx={{
                  maxWidth: "92%",
                  ml: "auto",
                  p: 1.25,
                  pb: 1,
                  borderRadius: "8px 0 8px 8px",
                  bgcolor: "#d9fdd3",
                  boxShadow: "0 1px 0.5px rgba(11,20,26,.13)",
                  fontFamily: FONT,
                  fontSize: 13,
                  lineHeight: 1.55,
                  color: "#111b21",
                  wordBreak: "break-word",
                }}
              >
                {preview.trim() ? (
                  renderWhatsApp(preview)
                ) : (
                  <Box component="span" sx={{ color: FAINT }}>Nothing to send yet.</Box>
                )}
              </Box>
            </Box>
            {unfilled.length > 0 && (
              <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#b45309", mt: 0.75, lineHeight: 1.5 }}>
                {unfilled.map((k) => `{${k}}`).join(", ")} — nothing to fill this in for this job, so
                it will be sent as written.
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>

      {/* ── Footer: always on screen ───────────────────────────────────── */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        spacing={1.5}
        sx={{ px: 3, py: 1.5, borderTop: `1px solid ${HAIR}`, bgcolor: "#fafafa" }}
      >
        {/* Where chats open on this computer. Saved the moment it is picked. */}
        {opener ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: MUTED, whiteSpace: "nowrap" }}>
              Open chats in
            </Typography>
            <Stack
              direction="row"
              role="radiogroup"
              aria-label="Open chats in"
              sx={{ border: `1px solid ${LINE}`, borderRadius: 2, overflow: "hidden", bgcolor: "#fff" }}
            >
              {CHAT_OPENERS.map((o) => {
                const on = opener === o.key;
                return (
                  <Box
                    key={o.key}
                    component="button"
                    type="button"
                    role="radio"
                    aria-checked={on}
                    title={o.hint}
                    onClick={() => onOpenerChange?.(o.key)}
                    sx={{
                      border: 0,
                      px: 1.25,
                      py: 0.6,
                      cursor: "pointer",
                      fontFamily: FONT,
                      fontSize: 12.5,
                      fontWeight: on ? 700 : 550,
                      color: on ? "#fff" : BODY,
                      bgcolor: on ? TEAL : "transparent",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {o.key === "app" ? "Desktop app" : "WhatsApp Web"}
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        ) : null}

        <Box sx={{ flex: 1 }} />

        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          {current && (
            <Button onClick={() => save(null)} disabled={saving} sx={{ ...textBtnSx, color: "#b91c1c" }}>
              Turn off
            </Button>
          )}
          <Button onClick={onClose} disabled={saving} sx={textBtnSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={saving || !draft.trim()}
            onClick={() => save(draft)}
            sx={{
              fontFamily: FONT,
              textTransform: "none",
              fontWeight: 700,
              px: 2.5,
              bgcolor: TEAL,
              "&:hover": { bgcolor: "#0f766e" },
            }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
